import {
	ReactElement,
	useMemo,
	useState
} from "react";
import {
	ActionListItem,
	DropOverActionList
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { Guid } from "client_legacy/ndaw/guid";
import { isNil } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { GroupSelectionMenu } from "./GroupSelectionMenu";
import {
	excludeGroupButPromoteChildren,
	getGroupsExcludingItself,
	HubItemGroupNodeFormModel
} from "./common";
import { OpenItemMenuState, useItemDropOverMenuProps } from "client/Hooks";

export type GroupItemMenuProps = {
	target: HTMLElement;
	onClose: () => void;
	menuState: OpenItemMenuState;
	groupId: Guid;
	groupsHierarchy: readonly HubItemGroupNodeFormModel[];
	containingGroupId?: Guid;
	onCreateCategory(): void;
	onCreateGroup(): void;
	onEdit: () => void;
	onDelete(): void;
	onMoveToGroup: (targetGroupId: Guid) => void;
	onMoveToTop?(): void;
	trackingId: string;
};

export const GroupItemMenu = (props: GroupItemMenuProps): ReactElement => {
	const {
		target,
		onClose,
		menuState,
		groupId,
		containingGroupId,
		groupsHierarchy,
		onCreateCategory,
		onCreateGroup,
		onEdit,
		onDelete,
		onMoveToGroup,
		onMoveToTop,
		trackingId
	} = props;

	const [view, setView] = useState<View>("main");

	const {
		contentAlignment,
		targetAlignment,
		closeOnMouseDown
	} = useItemDropOverMenuProps(
		menuState,
		{
			moreMenuTargetAlignment: "br",
			moreMenuContentAlignment: "tr"
		}
	);

	const moveToGroupTitle: string = localize(
		isNil(containingGroupId)
			? "_MoveToGroup"
			: "_MoveToAnotherGroup"
	);

	const groupsHierarchyToRender = useMemo(
		(): readonly HubItemGroupNodeFormModel[] => getGroupsExcludingItself(groupId, groupsHierarchy),
		[groupId, groupsHierarchy]
	);

	const doesAnyGroupExistToMoveIn = useMemo(
		(): boolean => isNil(containingGroupId)
			? groupsHierarchyToRender.length > 0
			: excludeGroupButPromoteChildren(containingGroupId, groupsHierarchyToRender)
				.length > 0,
		[containingGroupId, groupsHierarchyToRender]
	);

	const renderItems = (): ReactElement | null => {
		switch(view) {
			case "main":
				return (
					<>
						{onMoveToTop && (
							<ActionListItem
								onClick={onMoveToTop}
								text={localize("_MoveToTopLevel")}
								trackingId="move-to-top-level"
								icon="arrow-up-to-line"
							/>
						)}
						{doesAnyGroupExistToMoveIn && (
							<ActionListItem
								onClick={() => setView("group-selection")}
								text={moveToGroupTitle}
								trackingId="move-to-another-group"
								icon="arrow-right-to-line"
								rightIcon="chevron-right"
								shouldNotAutoCloseContainingDropOver
							/>
						)}
						<ActionListItem
							onClick={onCreateCategory}
							text={localize("_CreateCategory")}
							trackingId="create-category-button"
							icon="note-sticky"
						/>
						<ActionListItem
							onClick={onCreateGroup}
							text={localize("_CreateGroup")}
							trackingId="create-group"
							icon="layer-group"
						/>

						<ActionListItem
							onClick={onEdit}
							text={localize("_Edit")}
							trackingId="edit-group"
							icon="pen"
						/>

						<ActionListItem
							onClick={onDelete}
							text={localize("_Delete")}
							trackingId="delete-group"
							icon="trash-can"
						/>
					</>
				);
			case "group-selection":
				return onMoveToGroup
					? (
						<GroupSelectionMenu
							containingGroupId={containingGroupId}
							groupsHierarchy={groupsHierarchyToRender}
							moveToGroupTitle={moveToGroupTitle}
							onMoveToGroup={onMoveToGroup}
							onBack={() => setView("main")}
						/>
					)
					: null;
			default:
				never(view);
		}
	};

	return (
		<DropOverActionList
			target={target}
			onClose={onClose}
			menuTitle={localize("_Add")}
			targetAlignment={targetAlignment}
			contentAlignment={contentAlignment}
			closeOnMouseDown={closeOnMouseDown}
			trackingId={trackingId}
		>
			{renderItems()}
		</DropOverActionList>
	);
};
GroupItemMenu.displayName = "GroupItemMenu";

type View = "main" | "group-selection";